/**
 * ContractorModal — Company roster mutations are routed through
 * contractors-api. The modal is owned by Settings; event Crew only selects
 * existing roster entries.
 */
import { useEffect, useMemo, useState } from "react";
import { cx } from "@shared/cx";
import { Button } from "../ui/Button";
import { Select } from "../ui/Select";
import forms from "../ui/field.module.css";
import styles from "./ContractorModal.module.css";
import { saveContractor, type Contractor } from "../../lib/contractors-api";
import {
  listJobSheetTemplates,
  type JobSheetTemplate,
} from "../../lib/job-sheet-templates-api";
import { useDismissable } from "../../lib/use-dismissable";
import { useModalScrollLock } from "../../lib/use-modal-scroll-lock";
import { useMutationRunner } from "../../lib/use-mutation-runner";
import { validateContactFields } from "./validators";
import { InlineBanner } from "../ui/InlineBanner";

interface ContractorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSaved: (contractor: Contractor) => void;
  contractor?: Contractor | null;
}

export function ContractorModal({
  isOpen,
  onClose,
  onSaved,
  contractor,
}: ContractorModalProps) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [skills, setSkills] = useState("");
  const [bankAccount, setBankAccount] = useState("");
  // "" = inherit the Company default template (#188).
  const [jobsheetTemplateId, setJobsheetTemplateId] = useState("");
  const [templates, setTemplates] = useState<JobSheetTemplate[] | null>(null);
  const [templatesError, setTemplatesError] = useState<string | null>(null);
  // Modal-local runner: the error renders inline (a page-level banner would
  // sit behind the overlay); no refetch — onSaved updates the caller's state.
  const mutations = useMutationRunner();
  const saving = mutations.isPending("save-contractor");
  // Reveal-then-live: hidden until first submit, then recomputed every render.
  const [showValidation, setShowValidation] = useState(false);

  const isEditing = !!contractor;

  const fieldErrors = useMemo(() => {
    const errors = validateContactFields({ name, email, phone });
    if (email.trim() === "") errors.email = "Email is required";
    return errors;
  }, [name, email, phone]);

  useEffect(() => {
    if (isOpen && contractor) {
      setName(contractor.name);
      setEmail(contractor.email);
      setPhone(contractor.phone || "");
      setSkills(contractor.skills?.join(", ") || "");
      setBankAccount(contractor.bank_account || "");
      setJobsheetTemplateId(contractor.jobsheet_template_id || "");
    } else if (isOpen) {
      setName("");
      setEmail("");
      setPhone("");
      setSkills("");
      setBankAccount("");
      setJobsheetTemplateId("");
    }
    mutations.setError(null);
    setShowValidation(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mutations.setError is stable per render; depending on `mutations` would re-run on every pending change
  }, [isOpen, contractor]);

  // Template picker options (#188), loaded once per open.
  useEffect(() => {
    if (!isOpen) return;
    const ac = new AbortController();
    listJobSheetTemplates(ac.signal)
      .then((rows) => {
        setTemplates(rows);
        setTemplatesError(null);
      })
      .catch((err) => {
        if (err instanceof DOMException && err.name === "AbortError") return;
        setTemplatesError(err instanceof Error ? err.message : String(err));
      });
    return () => ac.abort();
  }, [isOpen]);

  // Lock background scroll while open (DESIGN-SYSTEM.md §Modals) and close on Escape.
  // The dismissal stack keeps Escape on the topmost surface, so an open Select
  // or a layered ConfirmModal closes first.
  useModalScrollLock(isOpen);
  useDismissable({ open: isOpen, onDismiss: onClose, pointer: "none" });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (Object.keys(fieldErrors).length > 0) {
      setShowValidation(true);
      return;
    }

    await mutations.run("save-contractor", "Save contractor", async () => {
      const skillsArray = skills
        .split(",")
        .map((s) => s.trim())
        .filter((s) => s.length > 0);

      const saved = await saveContractor({
        ...(isEditing ? { id: contractor.id } : {}),
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim() || null,
        skills: skillsArray.length > 0 ? skillsArray : null,
        bank_account: bankAccount.trim() || null,
        jobsheet_template_id: jobsheetTemplateId || null,
      });

      onSaved({
        id: saved.id,
        name: saved.name,
        email: saved.email,
        phone: saved.phone,
        skills: saved.skills ?? [],
        bank_account: saved.bank_account ?? null,
        jobsheet_template_id: saved.jobsheet_template_id ?? null,
      });
      onClose();
    });
  };

  if (!isOpen) return null;

  return (
    <div
      className={styles.overlay}
      onClick={onClose}
    >
      <div
        className={styles.panel}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className={styles.heading}>
          {isEditing ? "Edit Contractor" : "Add New Contractor"}
        </h2>
        <form onSubmit={handleSubmit} className={styles.form}>
          <div>
            <label className={forms.label}>
              Name
            </label>
            <input
              type="text"
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              aria-invalid={
                (showValidation && Boolean(fieldErrors.name)) || undefined
              }
              className={cx(
                forms.input,
                showValidation && fieldErrors.name && forms.invalid,
              )}
            />
            {showValidation && fieldErrors.name && (
              <InlineBanner variant="error">{fieldErrors.name}</InlineBanner>
            )}
          </div>
          <div>
            <label className={forms.label}>
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              aria-invalid={
                (showValidation && Boolean(fieldErrors.email)) || undefined
              }
              className={cx(
                forms.input,
                showValidation && fieldErrors.email && forms.invalid,
              )}
            />
            {showValidation && fieldErrors.email && (
              <InlineBanner variant="error">{fieldErrors.email}</InlineBanner>
            )}
          </div>
          <div>
            <label className={forms.label}>
              Phone{" "}
              <span className={forms.optional}>(optional)</span>
            </label>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              aria-invalid={
                (showValidation && Boolean(fieldErrors.phone)) || undefined
              }
              className={cx(
                forms.input,
                showValidation && fieldErrors.phone && forms.invalid,
              )}
            />
            {showValidation && fieldErrors.phone && (
              <InlineBanner variant="error">{fieldErrors.phone}</InlineBanner>
            )}
          </div>
          <div>
            <label className={forms.label}>
              Skills{" "}
              <span className={forms.optional}>
                (optional, comma separated)
              </span>
            </label>
            <input
              type="text"
              value={skills}
              onChange={(e) => setSkills(e.target.value)}
              className={forms.input}
            />
          </div>
          <div>
            <label className={forms.label}>
              Bank Account{" "}
              <span className={forms.optional}>(optional)</span>
            </label>
            <input
              type="text"
              value={bankAccount}
              onChange={(e) => setBankAccount(e.target.value)}
              className={forms.input}
            />
          </div>
          <div>
            <label
              className={forms.label}
              htmlFor="contractor-jobsheet-template"
            >
              Default Job sheet
            </label>
            <Select
              id="contractor-jobsheet-template"
              value={jobsheetTemplateId}
              disabled={templates === null}
              onChange={setJobsheetTemplateId}
              options={[
                { value: "", label: "Use Company default" },
                ...(templates ?? []).map((t) => ({
                  value: t.id,
                  label: t.name,
                })),
              ]}
            />
            {templatesError && (
              <InlineBanner variant="error">{templatesError}</InlineBanner>
            )}
            <p className={styles.hint}>
              A Job can override this for one Assignment.
            </p>
          </div>
          {mutations.error && (
            <p className={styles.error}>{mutations.error}</p>
          )}
          <div className={styles.footer}>
            <Button
              type="button"
              variant="secondary"
              onClick={onClose}
              className={styles.footerButton}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              loading={saving}
              className={styles.footerButton}
            >
              {isEditing ? "Save Changes" : "Add Contractor"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
